// Admin API client — /admin/orgs/* (mirrors the API's orgs aggregate).

import type {
  AdminOrganization,
  AdminOrganizationDetail,
  AdminSupportAccessEntry,
  OrgRole,
  PlanId,
} from '@a7y/types';
import { client, PaginatedResponse } from './transport';

export const orgsApi = {
  // Organizations
  getOrganizations: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    includeApptonomy?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('offset', String((params.page - 1) * (params.limit || 20)));
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params?.includeApptonomy) searchParams.set('includeApptonomy', 'true');
    return client.get<PaginatedResponse<AdminOrganization>>(`/orgs?${searchParams}`);
  },
  getOrganizationDetail: (id: string) => client.get<AdminOrganizationDetail>(`/orgs/${id}`),
  updateOrganization: (id: string, data: Partial<AdminOrganization>) =>
    client.patch<AdminOrganization>(`/orgs/${id}`, data),
  setOverridePlan: (orgId: string, overridePlanId: PlanId | null) =>
    client.post<{ message: string; orgId: string; overridePlanId: PlanId | null }>(
      `/orgs/${orgId}/override-plan`,
      { overridePlanId }
    ),
  setTrialEndDate: (orgId: string, trialEndsAt: string | null) =>
    client.post<{ message: string; orgId: string; trialEndsAt: string | null }>(
      `/orgs/${orgId}/trial-end-date`,
      { trialEndsAt }
    ),
  suspendOrganization: (id: string) => client.post<void>(`/orgs/${id}/suspend`),
  activateOrganization: (id: string) => client.post<void>(`/orgs/${id}/reinstate`),
  // Support Access (A7Y-1274)
  listSupportAccess: (orgId: string) =>
    client.get<{ data: AdminSupportAccessEntry[] }>(`/orgs/${orgId}/support-access`),
  grantSupportAccess: (
    orgId: string,
    body: { userId: string; role: OrgRole; reason: string; ttlDays: number }
  ) => client.post<{ data: AdminSupportAccessEntry }>(`/orgs/${orgId}/support-access`, body),
  revokeSupportAccess: (orgId: string, grantId: string) =>
    client.delete<{ success: boolean }>(`/orgs/${orgId}/support-access/${grantId}`),
};
